import api from './api';
import { flagEmoji } from '../components/events/eventUtils';

// Deutsche Nationalitätsnamen → ISO/Override-Code für flagEmoji.
// FlashLive liefert oft KEINEN country.code (nur den deutschen Namen), daher
// dieser Lookup für die Pro-Fallback-Profile (Darts-relevante Nationen).
const NATIONALITY_TO_CODE: Record<string, string> = {
  england: 'ENG', schottland: 'SCO', wales: 'WAL', nordirland: 'GB',
  deutschland: 'DE', niederlande: 'NL', belgien: 'BE', österreich: 'AT',
  schweiz: 'CH', irland: 'IE', polen: 'PL', australien: 'AU', neuseeland: 'NZ',
  usa: 'US', 'vereinigte staaten': 'US', kanada: 'CA', japan: 'JP',
  litauen: 'LT', lettland: 'LV', estland: 'EE', tschechien: 'CZ', ungarn: 'HU',
  schweden: 'SE', dänemark: 'DK', finnland: 'FI', norwegen: 'NO', spanien: 'ES',
  portugal: 'PT', italien: 'IT', frankreich: 'FR', kroatien: 'HR',
  gibraltar: 'GI', südafrika: 'ZA', hongkong: 'HK', philippinen: 'PH',
  singapur: 'SG', indien: 'IN', china: 'CN', brasilien: 'BR', ukraine: 'UA',
  türkei: 'TR', griechenland: 'GR', rumänien: 'RO', bulgarien: 'BG',
  slowakei: 'SK', slowenien: 'SI', serbien: 'RS', island: 'IS', luxemburg: 'LU',
};

/** Flag-Emoji für ein Pro-Fallback-Profil aus Nationalität ODER Code ableiten. */
function deriveFlag(nationality?: string | null, code?: string | null): string | null {
  const byName = nationality ? NATIONALITY_TO_CODE[nationality.trim().toLowerCase()] : null;
  return flagEmoji(byName || code || null);
}

// WICHTIG: Alle Felder nullable für maximale Backwards-Compatibility.
export interface PlayerProfileCareerStats {
  averageAllTime?: number | null;
  highestAverage?: number | null;
  highestCheckout?: number | null;
  nineDarters?: number | null;
  majorTitles?: number | null;
  worldChampionshipTitles?: number | null;
  worldCupTitles?: number | null;
  matchplayTitles?: number | null;
  premierLeagueTitles?: number | null;
}

export interface PlayerProfileMajorTitle {
  tournament?: string | null;
  year?: number | null;
  opponent?: string | null;
  score?: string | null;
}

export interface PlayerProfileSocial {
  instagram?: string | null;
  twitter?: string | null;
  facebook?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
  website?: string | null;
}

export interface PlayerProfile {
  _id?: string;
  id?: string;
  slug?: string;
  createdAt?: number;
  updatedAt?: number | null;
  createdBy?: string | null;

  name?: string;
  firstname?: string | null;
  lastname?: string | null;
  nickname?: string | null;
  // Nur bei Pro-Fallback-Profilen gesetzt (aus ProPlayer/FlashLive, kein
  // redaktionelles Profil) — dient dem zuverlässigen DartConnect-Stats-Match.
  flashliveId?: string | null;
  walkOnSong?: string | null;
  dateOfBirth?: number | null;
  deathDate?: number | null;
  placeOfBirth?: string | null;

  nationality?: string | null;
  countryCode?: string | null;
  flag?: string | null;

  organization?: string | null;
  turnedPro?: number | null;
  status?: 'ACTIVE' | 'RETIRED' | 'INACTIVE' | 'DECEASED' | null;
  worldRanking?: number | null;
  careerHigh?: number | null;

  dartWeight?: number | null;
  dartBrand?: string | null;

  careerStats?: PlayerProfileCareerStats | null;
  majorTitles?: PlayerProfileMajorTitle[];

  profileImage?: string | null;
  heroImage?: string | null;
  images?: string[];

  bio?: string | null;
  socialMedia?: PlayerProfileSocial | null;

  seoTitle?: string | null;
  seoDescription?: string | null;

  visible?: boolean;
  verified?: boolean;
  tags?: string[];
  views?: number;
}

export interface PlayerProfileListResponse {
  players?: PlayerProfile[];
}

class PlayerProfileService {
  async list(params?: { limit?: number; offset?: number; search?: string }): Promise<PlayerProfile[]> {
    try {
      const res = await api.get<PlayerProfileListResponse>('/api/player-profiles', {
        params: {
          limit: params?.limit ?? 50,
          offset: params?.offset ?? 0,
          search: params?.search,
        },
      });
      return res.data?.players ?? [];
    } catch (err) {
      console.error('Fehler beim Laden der PlayerProfiles:', err);
      return [];
    }
  }

  async getById(id: string): Promise<PlayerProfile | null> {
    if (!id) return null;
    try {
      const res = await api.get<PlayerProfile>(`/api/player-profile/id/${id}`);
      return res.data ?? null;
    } catch (err) {
      console.error('Fehler beim Laden des PlayerProfiles by ID:', err);
      return null;
    }
  }

  async getBySlug(slug: string): Promise<PlayerProfile | null> {
    if (!slug) return null;
    try {
      const res = await api.get<PlayerProfile>(`/api/player-profile/slug/${slug}`);
      return res.data ?? null;
    } catch (err) {
      console.error('Fehler beim Laden des PlayerProfiles by Slug:', err);
      return null;
    }
  }

  /**
   * Pro-Fallback (2026-08-26): Hat ein Pro-Spieler KEIN redaktionelles Profil,
   * bauen wir aus den ProPlayer-/FlashLive-Stammdaten (/api/players/slug/:slug,
   * das per slug ODER flashliveId auflöst) ein minimales Profil — damit
   * /spieler/:slug NICHT fälschlich auf /community/profil umleitet.
   * Bewusst OHNE worldRanking: currentRanking ist NICHT die PDC Order of Merit.
   */
  async getProAsProfile(slugOrId: string): Promise<PlayerProfile | null> {
    if (!slugOrId) return null;
    try {
      const res = await api.get<any>(`/api/players/slug/${encodeURIComponent(slugOrId)}`);
      const data = res.data?.data ?? res.data;
      const p = data?.player;
      const db = data?.dbPlayer;
      if (!p && !db) return null;
      const name = (p?.name || db?.name || '').toString();
      if (!name) return null;
      const nationality = p?.countryName || db?.country?.name || null;
      const countryCode = db?.country?.code || null;
      return {
        slug: db?.slug || p?.slug || slugOrId,
        name,
        nationality,
        countryCode,
        flag: deriveFlag(nationality, countryCode),
        profileImage: p?.image || db?.image || null,
        organization: db?.organization || null,
        flashliveId: p?.flashliveId || db?.flashliveId || null,
        status: 'ACTIVE',
        visible: true,
      };
    } catch (err) {
      console.error('Fehler beim Pro-Fallback-Profil:', err);
      return null;
    }
  }

  // === Helpers ===
  displayName(p?: PlayerProfile | null): string {
    if (!p) return '';
    if (p.name && typeof p.name === 'string') return p.name;
    if (p.firstname || p.lastname) return [p.firstname, p.lastname].filter(Boolean).join(' ');
    return '';
  }

  displayNickname(p?: PlayerProfile | null): string | null {
    if (!p?.nickname || typeof p.nickname !== 'string') return null;
    return p.nickname.trim() || null;
  }

  flag(p?: PlayerProfile | null): string {
    return p?.flag || '🏳️';
  }

  buildFileUrl(fileId?: string | null): string | null {
    if (!fileId) return null;
    if (/^https?:\/\//i.test(fileId)) return fileId;
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');
    return `${base}/api/file/${fileId}`;
  }
}

export default new PlayerProfileService();
